import React, { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material'
import { GridView } from '@mui/icons-material'
import {
  CampaignResponse,
  CampaignStatus,
  ErrorResponse
} from '../../types/campaign'
import {
  fetchCampaignList,
  fetchOwners,
  updateCampaignStatus
} from '../getData'
import { getTotalBudget, formatDate } from '../utils'
import CircularLoader from '../CircularLoader'
import StatusChip from '../StatusChip'
import AlertMessage, { AlertContents } from '../AlertMessage'
import CampaignsPlaceholder from '../icons/CampaignsPlaceholder'
import theme from '../theme'
import { AxiosError } from 'axios'

const CampaignList = () => {
  const navigate = useNavigate()
  const [alertContents, setAlertContents] = useState<AlertContents | null>(null)
  const [ownerFilter, setOwnerFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  })

  const {
    data: campaigns,
    isLoading,
    isError,
    refetch: refetchCampaigns
  } = useQuery({
    queryKey: [
      'campaigns',
      statusFilter,
      ownerFilter,
      searchFilter,
      paginationModel.page
    ],
    queryFn: () =>
      fetchCampaignList(
        statusFilter.length > 0 ? statusFilter : undefined,
        ownerFilter.length > 0 ? ownerFilter : undefined,
        searchFilter || undefined,
        paginationModel.page
      )
  })

  const { data: owners } = useQuery({
    queryKey: ['owners'],
    queryFn: () => fetchOwners(),
    retry: false
  })

  const {
    mutate: updateCampaignStatusMutation,
    isPending: isPendingStatusUpdate
  } = useMutation({
    mutationFn: (params: { status: string; id: number }) =>
      updateCampaignStatus(params.id, params.status),
    onSuccess: () => {
      refetchCampaigns()
      setAlertContents({
        open: true,
        message: 'Campaign status updated successfully',
        severity: 'success'
      })
    },
    onError: (error: AxiosError) => {
      console.error(error)
      const errorData = error.response?.data as ErrorResponse
      setAlertContents({
        open: true,
        message:
          errorData.context.join('<br />') ||
          'Failed to update campaign status',
        severity: 'error'
      })
    }
  })

  const hasFilters =
    searchFilter || ownerFilter.length > 0 || statusFilter.length > 0

  if (isLoading && !hasFilters && paginationModel.page === 0) {
    return <CircularLoader />
  }

  const pageHeader = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '60px 40px 0 40px'
      }}
    >
      <Box>
        <GridView
          sx={{
            color: theme.palette['sage-600'],
            marginRight: theme.space['28'],
            height: '28px',
            width: '28px'
          }}
        />
        <Typography variant="heading-8">Campaigns</Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        sx={{ height: '56px', width: '136px' }}
        onClick={() => navigate('/campaign/create')}
      >
        <Typography variant="ui-3">Create</Typography>
      </Button>
    </Box>
  )

  if (isError && !hasFilters) {
    return (
      <Box>
        {pageHeader}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.space['40'],
            backgroundColor: theme.palette.background.paper,
            padding: theme.space['80'],
            margin: theme.space['40'],
            borderRadius: theme.borders['rounded-10'],
            color: theme.palette['sage-400']
          }}
        >
          <CampaignsPlaceholder sx={{ fontSize: '20rem' }} />
          <Typography
            variant="heading-2"
            sx={{ color: theme.palette['red-500'] }}
          >
            There has been an error loading the campaigns list.
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!campaigns && !hasFilters && paginationModel.page === 0) {
    return (
      <Box>
        {pageHeader}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.space['40'],
            backgroundColor: theme.palette.background.paper,
            padding: theme.space['80'],
            margin: theme.space['40'],
            borderRadius: theme.borders['rounded-10'],
            color: theme.palette['sage-400']
          }}
        >
          <CampaignsPlaceholder sx={{ fontSize: '20rem' }} />
          <Typography variant="heading-2">No campaigns were found.</Typography>
        </Box>
      </Box>
    )
  }

  const resetPagination = () => {
    if (paginationModel.page > 0) {
      setPaginationModel({
        ...paginationModel,
        page: 0
      })
    }
  }

  const renderColumnHeader = (text: string) => {
    return (
      <Typography
        variant="body-2"
        sx={{
          color: theme.palette['sage-600'],
          textTransform: 'uppercase'
        }}
      >
        {text}
      </Typography>
    )
  }

  const renderCell = (text: string | number) => {
    return (
      <Typography
        title={text.toString()}
        variant="body-10"
        sx={{
          color: theme.palette['base-black'],
          display: 'block',
          textWrap: 'wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </Typography>
    )
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200,
      type: 'string',
      renderHeader: () => renderColumnHeader('Name'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(params.row.name)
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      type: 'string',
      renderHeader: () => renderColumnHeader('Status'),
      renderCell: (params: { row: CampaignResponse }) => (
        <StatusChip
          status={params.row.status}
          onStatusChange={(status) =>
            updateCampaignStatusMutation({ status, id: params.row.id })
          }
          isUpdating={isPendingStatusUpdate}
        />
      )
    },
    {
      field: 'owner',
      headerName: 'Owner',
      flex: 1,
      minWidth: 150,
      type: 'string',
      valueGetter: (params: { row: CampaignResponse }) => params.row.owner.name,
      renderHeader: () => renderColumnHeader('Owner'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(params.row.owner.name)
    },
    {
      field: 'advertiser',
      headerName: 'Advertiser',
      flex: 1,
      minWidth: 150,
      type: 'string',
      valueGetter: (params: { row: CampaignResponse }) =>
        params.row.advertiser.name,
      renderHeader: () => renderColumnHeader('Advertiser'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(params.row.advertiser.name)
    },
    {
      field: 'calculatedBudget',
      headerName: 'Budget',
      flex: 1,
      minWidth: 120,
      valueGetter: (params: { row: CampaignResponse }) =>
        params.row.calculated_budget,
      renderHeader: () => renderColumnHeader('Budget'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(getTotalBudget(params.row))
    },
    {
      field: 'start_date',
      headerName: 'Start',
      flex: 1,
      minWidth: 120,
      type: 'date',
      valueGetter: (params: { row: CampaignResponse }) =>
        params.row.calculated_duration.starts_at
          ? new Date(params.row.calculated_duration.starts_at)
          : null,
      renderHeader: () => renderColumnHeader('Start'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(formatDate(params.row.calculated_duration.starts_at, params.row.flights[0]?.time_zone))
    },
    {
      field: 'end_date',
      headerName: 'End',
      flex: 1,
      minWidth: 120,
      type: 'date',
      valueGetter: (params: { row: CampaignResponse }) =>
        params.row.calculated_duration.ends_at
          ? new Date(params.row.calculated_duration.ends_at)
          : null,
      renderHeader: () => renderColumnHeader('End'),
      renderCell: (params: { row: CampaignResponse }) =>
        renderCell(formatDate(params.row.calculated_duration.ends_at, params.row.flights[0]?.time_zone))
    }
  ]

  return (
    <Box>
      {alertContents?.open && (
        <AlertMessage
          alert={alertContents}
          onClose={() => setAlertContents(null)}
          backgroundColor={theme.palette.background.default}
        />
      )}
      {isPendingStatusUpdate && <CircularLoader />}
      {pageHeader}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.borders['rounded-10'],
          margin: theme.space['40']
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            padding: theme.space['40'],
            gap: theme.space['20'],
            borderBottom: `1px solid ${theme.palette['sage-100']}`
          }}
        >
          <TextField
            variant="outlined"
            sx={{ flex: 2 }}
            value={searchFilter}
            onChange={(event) => {
              setSearchFilter(event.target.value)
              resetPagination()
            }}
            placeholder="Search by name, advertiser, or Hubspot ID"
          />
          {owners && owners.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: theme.space['12']
              }}
            >
              <Typography
                variant="body-11"
                sx={{ color: theme.palette['sage-400'] }}
              >
                Owner
              </Typography>
              <Select
                variant="outlined"
                placeholder="Select Owner"
                value={ownerFilter}
                onChange={(event) => {
                  setOwnerFilter(event.target.value as string[])
                  resetPagination()
                }}
                multiple
                data-testid="owner-filter-select"
              >
                {owners?.map((owner) => (
                  <MenuItem key={owner.id} value={owner.id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              gap: theme.space['12']
            }}
          >
            <Typography
              variant="body-11"
              sx={{ color: theme.palette['sage-400'] }}
            >
              Status
            </Typography>
            <Select
              variant="outlined"
              placeholder="Select Status"
              value={statusFilter}
              multiple
              onChange={(event) => {
                setStatusFilter(event.target.value as string[])
                resetPagination()
              }}
            >
              {Object.values(CampaignStatus).map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        {campaigns && campaigns.length > 0 && (
          <DataGrid
            rows={campaigns || []}
            columns={columns}
            rowCount={100}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
            }}
            pageSizeOptions={[10]}
            isRowSelectable={(row) =>
              row.row.status !== CampaignStatus.Deleted &&
              !isPendingStatusUpdate
            }
            onRowClick={({ row }) => {
              if (row.status !== CampaignStatus.Deleted) {
                navigate(`/campaign/${row.id}`)
              }
            }}
            sx={{
              border: 'none',
              width: '100%',
              '& .MuiDataGrid-columnHeader:first-of-type': {
                paddingLeft: theme.space['40']
              },
              '& .MuiDataGrid-columnHeader:last-of-type': {
                paddingRight: theme.space['40']
              },
              '& .MuiDataGrid-row': {
                '& .MuiDataGrid-cell:first-of-type': {
                  paddingLeft: theme.space['40']
                },
                '& .MuiDataGrid-cell:last-of-type': {
                  paddingRight: theme.space['40']
                }
              }
            }}
          />
        )}
        {campaigns && campaigns.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.space['40'],
              backgroundColor: theme.palette.background.paper,
              padding: theme.space['80'],
              borderRadius: theme.borders['rounded-10'],
              color: theme.palette['sage-400']
            }}
          >
            <CampaignsPlaceholder sx={{ fontSize: '20rem' }} />
            <Typography variant="heading-2">
              No campaigns were found with selected filters.
            </Typography>
          </Box>
        )}
        {(hasFilters || paginationModel.page > 0) && isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '630px'
            }}
          >
            <CircularProgress sx={{ color: theme.palette['accent-500'] }} />
          </Box>
        )}
        {hasFilters && isError && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.space['40'],
              backgroundColor: theme.palette.background.paper,
              padding: theme.space['80'],
              borderRadius: theme.borders['rounded-10'],
              color: theme.palette['sage-400']
            }}
          >
            <CampaignsPlaceholder sx={{ fontSize: '20rem' }} />
            <Typography
              variant="heading-2"
              sx={{ color: theme.palette['red-500'] }}
            >
              There was an error loading the campaigns list.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CampaignList
